import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { storyApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Trash2, Edit, Plus, LogOut, Menu, AlertCircle, CheckCircle, Copy, Eye, Download, ListOrdered } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { StoryCard } from '../components/StoryCard';
import { StatusDropdown } from '../components/StatusDropdown';
import { withSuccessToast, successMessages } from '../utils/apiHelpers';
import { isFeatureEnabled } from '../config/features';
import { copyToClipboard, getPublicStoryUrl } from '../utils/clipboard';
import { SlidesViewModal } from '../components/SlidesViewModal';
import { PublishReorderModal } from '../components/PublishReorderModal';
import { ImportPasswordDialog } from '../components/ImportPasswordDialog';

interface Story {
  _id: string;
  title: string;
  one_line_brief: string;
  status: string;
  category_id?: { _id: string; name: string };
  subcat_id?: { _id: string; name: string };
  category_name?: string;
  subcategory_name?: string;
  noOfSlides: number;
  created_at: string;
  updated_at: string;
  public_token: string;
  tags: string[];
  isComplete?: boolean;
  incompleteSlides?: number;
  publishedFor?: string | null;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toast, error } = useToast();
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [viewModalStory, setViewModalStory] = useState<{ id: string; title: string } | null>(null);
  const [publishReorderOpen, setPublishReorderOpen] = useState(false);
  const [importPasswordOpen, setImportPasswordOpen] = useState(false);
  const [importPassword, setImportPassword] = useState('');
  const [autoExport, setAutoExport] = useState<boolean>(() => {
    const saved = localStorage.getItem('autoExportEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    fetchStories();
  }, [statusFilter]);

  const fetchStories = async () => {
    try {
      const response = await storyApi.getStories(
        statusFilter ? { status: statusFilter } : undefined
      );
      // New API structure: { data: {...}, success, message }
      let stories = response.data.data || [];
      
      // If flexible drafting is enabled, fetch completion status for draft stories
      if (isFeatureEnabled('FLEXIBLE_DRAFTING')) {
        const draftStories = stories.filter((s: Story) => s.status === 'draft');
        
        // Fetch validation status for each draft story
        const validationPromises = draftStories.map(async (story: Story) => {
          try {
            const validationRes = await storyApi.validateStory(story._id);
            return {
              id: story._id,
              isComplete: validationRes.data.isComplete,
              incompleteSlides: validationRes.data.incompleteSlides
            };
          } catch (error) {
            console.error(`Failed to validate story ${story._id}:`, error);
            return { id: story._id, isComplete: true, incompleteSlides: 0 };
          }
        });
        
        const validationResults = await Promise.all(validationPromises);
        
        // Merge validation results with stories
        stories = stories.map((story: Story) => {
          const validation = validationResults.find(v => v.id === story._id);
          if (validation) {
            return { ...story, ...validation };
          }
          return story;
        });
      }
      
      setStories(stories);
    } catch (error) {
      console.error('Failed to fetch stories:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch stories',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (storyId: string, newStatus: string) => {
    // Frontend validation for ready_to_publish status
    if (newStatus === 'ready_to_publish') {
      const story = stories.find(s => s._id === storyId);
      if (story && story.isComplete === false) {
        error('Cannot mark as ready to publish: Story has incomplete slides');
        return;
      }
    }
    
    try {
      await withSuccessToast(
        storyApi.updateStoryStatus(storyId, newStatus),
        'Story status updated successfully'
      );
      fetchStories();
    } catch (error: any) {
      // Backend validation errors will be shown by the error toast
      if (error.response?.data?.error) {
        // Error already shown by API interceptor
      }
    }
  };

  const handleDelete = async (storyId: string) => {
    if (!window.confirm('Are you sure you want to delete this story?')) return;

    try {
      await withSuccessToast(
        storyApi.deleteStory(storyId),
        successMessages.storyDeleted
      );
      fetchStories();
    } catch (error) {
      // Error handled by API interceptor
    }
  };

  const handlePublish = async (storyId: string) => {
    try {
      const response = await storyApi.publishStory(storyId);
      toast({
        title: 'Success',
        description: `Story published! Public URL: ${response.data.publicUrl}`
      });
      fetchStories();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to publish story',
        variant: 'destructive'
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleExportData = async () => {
    try {
      const response = await storyApi.exportUserData();
      const data = response.data.data;

      // Get local datetime string and format it for filename
      const now = new Date();
      const localDateTime = now.toLocaleString('sv-SE').replace(/[:\s]/g, '-');
      const filename = `stories-export-${localDateTime}.json`;

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'Success',
        description: 'Data exported successfully!'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to export data',
        variant: 'destructive'
      });
    }
  };

  const handleAutoExportToggle = (checked: boolean) => {
    setAutoExport(checked);
    localStorage.setItem('autoExportEnabled', JSON.stringify(checked));
  };

  /**
   * Handle copying public link to clipboard
   * @param {string} publicToken - Story's public token
   */
  const handleCopyLink = async (publicToken: string) => {
    const url = getPublicStoryUrl(publicToken);
    const success = await copyToClipboard(url);
    
    if (success) {
      toast({
        title: 'Success',
        description: 'Link copied to clipboard!'
      });
    } else {
      toast({
        title: 'Error',
        description: 'Failed to copy link',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading stories...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card shadow-sm border-b">
        <div className="max-w-[1600px] mx-auto mobile-padding">
          <div className="flex justify-between items-center py-4 sm:py-6">
            <h1 className="heading-responsive font-bold text-foreground">My Stories</h1>
            
            {/* Desktop navigation */}
            <div className="hidden sm:flex items-center space-x-4">
              <span className="text-sm text-muted-foreground">Welcome, {user?.name}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/settings')}
              >
                Settings
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="sm:hidden touch-target"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
          
          {/* Mobile menu dropdown */}
          {mobileMenuOpen && (
            <div className="sm:hidden py-3 border-t space-y-3">
              <div className="text-sm text-muted-foreground">
                Welcome, {user?.name}
              </div>
              <div className="flex flex-col space-y-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/settings')}
                  className="w-full justify-start"
                >
                  Settings
                </Button>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="w-full justify-start"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto py-4 sm:py-6 mobile-padding">
        <div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <label className="text-sm font-medium text-muted-foreground">Filter by status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="block w-full sm:w-40 max-w-full rounded-md border-input bg-background shadow-sm focus:border-primary focus:ring-primary text-sm touch-target"
              >
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="ready_to_publish">Ready to Publish</option>
                <option value="published">Published</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setImportPasswordOpen(true)}
                variant="outline"
                size="sm"
                className="text-muted-foreground"
                title="Publish and reorder stories via CSV"
              >
                <ListOrdered className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">Publish-Reorder</span>
              </Button>
              <Button
                onClick={handleExportData}
                variant="outline"
                size="sm"
                className="text-muted-foreground"
                title="Export all data"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">Export (json)</span>
              </Button>
              <Button
                onClick={() => navigate('/stories/new')}
                className="w-full sm:w-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add New Story
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              id="autoExport"
              checked={autoExport}
              onChange={(e) => handleAutoExportToggle(e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <label htmlFor="autoExport" className="text-sm text-muted-foreground">
              Auto-export after every save
            </label>
          </div>

          {stories.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-lg shadow-sm border">
              <h3 className="text-lg font-medium text-foreground mb-2">No stories yet</h3>
              <p className="text-muted-foreground mb-4">Create your first story to get started</p>
              <Button onClick={() => navigate('/stories/new')}>
                <Plus className="h-4 w-4 mr-2" />
                Create Story
              </Button>
            </div>
          ) : (
            <>
              {/* Mobile view - Cards */}
              <div className="sm:hidden space-y-4">
                {stories.map((story) => (
                  <StoryCard
                    key={story._id}
                    story={story}
                    onEdit={(id) => navigate(`/stories/${id}/edit`)}
                    onDelete={handleDelete}
                    onStatusChange={handleStatusChange}
                    onView={setViewModalStory}
                  />
                ))}
              </div>
              
              {/* Desktop view - Table */}
              <div className="hidden sm:block bg-card shadow-sm rounded-md border overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted/50">
                    <tr>
                       <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider" >
                       <p style={{width:'50px'}}> Sr No.</p>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Brief
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Category
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Tags
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Slides
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[180px]">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Published for
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[200px]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                  {stories.map((story,i) => (
                    <tr key={story._id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {i +1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {story.title}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {story.one_line_brief || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {story.category_id?.name || story.category_name || '-'} / {story.subcat_id?.name || story.subcategory_name || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {story.tags?.filter(tag => tag).join(', ') || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {story.noOfSlides}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 min-w-[180px]">
                        <div className="flex items-center gap-2">
                          <StatusDropdown
                            currentStatus={story.status}
                            storyId={story._id}
                            onStatusChange={handleStatusChange}
                            isStoryComplete={story.isComplete !== false}
                            className="w-full"
                          />
                          {isFeatureEnabled('FLEXIBLE_DRAFTING') && story.status === 'draft' && story.isComplete !== undefined && (
                            story.isComplete ? (
                              <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                            ) : (
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <AlertCircle className="h-4 w-4 text-warning" />
                                {story.incompleteSlides !== undefined && story.incompleteSlides > 0 && (
                                  <span className="text-xs text-warning">({story.incompleteSlides})</span>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {story.status === 'published' && story.publishedFor ? 
                          new Date(story.publishedFor).toLocaleDateString('en-GB', { 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric' 
                          }).replace(/ /g, ' ') : 
                          '-'
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/stories/${story._id}/edit`)}
                          title="Edit story"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setViewModalStory({ id: story._id, title: story.title })}
                          title="View slides"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(story._id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                        {story.status === 'ready_to_publish' && !story.public_token && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePublish(story._id)}
                          >
                            Publish
                          </Button>
                        )}
                        {story.public_token && (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopyLink(story.public_token)}
                              title="Copy public link"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <a
                              href={`/stories/public/${story.public_token}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:text-primary/80 text-xs"
                            >
                              View Public
                            </a>
                          </div>
                        )}
                      </td>
                    </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </main>

      <ImportPasswordDialog
        isOpen={importPasswordOpen}
        onClose={() => setImportPasswordOpen(false)}
        onVerified={(password) => {
          setImportPassword(password);
          setImportPasswordOpen(false);
          setPublishReorderOpen(true);
        }}
      />

      <PublishReorderModal
        isOpen={publishReorderOpen}
        onClose={() => {
          setPublishReorderOpen(false);
          setImportPassword('');
        }}
        onImported={fetchStories}
        importPassword={importPassword}
      />

      {/* Slides View Modal */}
      {viewModalStory && (
        <SlidesViewModal
          isOpen={!!viewModalStory}
          onClose={() => setViewModalStory(null)}
          storyId={viewModalStory.id}
          storyTitle={viewModalStory.title}
        />
      )}
    </div>
  );
}